import { memo } from 'react'

import { cn } from '@/lib/utils'
import { GRID_COLS } from '../constants'
import type { PhoneLine } from '../types'

type HoverHandler = (lineNumber: number, rect: DOMRect) => void

const LineCell = memo(function LineCell({
  lineNumber,
  active,
  isHovered,
  cols,
  onHover,
  onLeave,
}: {
  lineNumber: number
  active: boolean
  isHovered: boolean
  /** Column count — drives the per-column stagger of the pulse wave. */
  cols: number
  onHover: HoverHandler
  onLeave: () => void
}) {
  return (
    <div
      onMouseEnter={(e) =>
        onHover(lineNumber, e.currentTarget.getBoundingClientRect())
      }
      onMouseLeave={onLeave}
      className={cn(
        // No cursor-pointer: cells aren't clickable, only hovered.
        'aspect-square rounded-[2px] border',
        active
          ? 'animate-pulse border-fuchsia-600 bg-fuchsia-500 dark:border-fuchsia-400'
          : 'border-slate-300 bg-slate-100 dark:border-slate-700 dark:bg-slate-800',
        isHovered && 'ring-2 ring-blue-500'
      )}
      // Staggered wave across columns (finished instantly under
      // prefers-reduced-motion by index.css).
      style={
        active
          ? { animationDelay: `${((lineNumber - 1) % cols) * 0.06}s` }
          : undefined
      }
    />
  )
})

/**
 * The full grid of phone lines (active pulsing, idle grey). Column count is
 * configurable (`cols`, default `GRID_COLS`) so a denser layout can pack more
 * lines per row / shrink the cells. memo() skips the grid on polls where line
 * data is unchanged (React Query keeps a stable `lines` ref via structural
 * sharing); cells are memoized too, so a hover only re-renders the two affected
 * cells.
 */
export const PhoneLineGrid = memo(function PhoneLineGrid({
  lines,
  hoveredLine,
  cols = GRID_COLS,
  onHover,
  onLeave,
}: {
  lines: PhoneLine[]
  hoveredLine: number | null
  cols?: number
  onHover: HoverHandler
  onLeave: () => void
}) {
  return (
    <div
      role="group"
      aria-label="Phone line grid"
      className="grid gap-0.5"
      // Dynamic column count can't be a static Tailwind class, so set the
      // template inline.
      style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
      {lines.map((line) => (
        <LineCell
          key={line.line_number}
          lineNumber={line.line_number}
          active={line.status === 'active'}
          isHovered={hoveredLine === line.line_number}
          cols={cols}
          onHover={onHover}
          onLeave={onLeave}
        />
      ))}
    </div>
  )
})
